import React, { useState } from 'react';
import styled from 'styled-components';
import { X, AlertTriangle } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 2rem;
`;

const Modal = styled.div`
  background: linear-gradient(135deg, #1a1f35 0%, #2B3857 100%);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(239, 68, 68, 0.3);
  overflow: hidden;
`;

const Header = styled.div`
  background: rgba(239, 68, 68, 0.1);
  padding: 1.5rem;
  border-bottom: 1px solid rgba(239, 68, 68, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  background: rgba(239, 68, 68, 0.2);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #EF4444;
`;

const Title = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const Body = styled.div`
  padding: 2rem;
`;

const WarningText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
`;

const ConfirmationSection = styled.div`
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ConfirmationLabel = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: 0.75rem;
  font-weight: 500;
`;

const ExpectedValue = styled.div`
  background: rgba(0, 0, 0, 0.3);
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-family: 'Courier New', monospace;
  color: #FCA5A5;
  font-weight: 600;
  border-left: 3px solid #EF4444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 2px solid ${props => props.hasError ? '#EF4444' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#EF4444' : '#C5337B'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.2)' : 'rgba(197, 51, 123, 0.2)'};
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ErrorMessage = styled.div`
  color: #FCA5A5;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Footer = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.1);
  color: #fff;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ConfirmButton = styled(Button)`
  background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%);
  color: white;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #DC2626 0%, #B91C1C 100%);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
    transform: translateY(-2px);
  }
`;

const RejectConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  contentTitle, 
  loading = false 
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (inputValue.trim().toLowerCase() !== contentTitle.trim().toLowerCase()) {
      setError('The text you entered does not match. Please try again.');
      return;
    }
    
    setError('');
    onConfirm();
  };

  const handleClose = () => {
    setInputValue('');
    setError('');
    onClose();
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (error) setError('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderContent>
            <IconWrapper>
              <AlertTriangle size={24} />
            </IconWrapper>
            <Title>Confirm Rejection</Title>
          </HeaderContent>
          <CloseButton onClick={handleClose}>
            <X size={24} />
          </CloseButton>
        </Header>
        
        <Body>
          <WarningText>
            You are about to reject this content submission. This action will prevent the content from being published to the website. Are you sure you want to proceed?
          </WarningText>
          
          <ConfirmationSection>
            <ConfirmationLabel>
              To confirm rejection, please type the content title exactly as shown below:
            </ConfirmationLabel>
            <ExpectedValue>{contentTitle}</ExpectedValue>
            <Input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Type the title here..."
              hasError={!!error}
              autoFocus
            />
            {error && (
              <ErrorMessage>
                <AlertTriangle size={14} />
                {error}
              </ErrorMessage>
            )}
          </ConfirmationSection>
        </Body>
        
        <Footer>
          <CancelButton onClick={handleClose} disabled={loading}>
            Cancel
          </CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={loading}>
            {loading ? 'Rejecting...' : 'Confirm Rejection'}
          </ConfirmButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default RejectConfirmationModal;

